import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Divider,
	FormControl,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useParams } from "react-router-dom";

import {
	CampaignBriefStatus,
	downloadBriefDocument,
	useAdminCampaignBrief,
	useMoveCampaignBrief,
} from "@/hooks/api/use-campaign-briefs";
import { RouterLink } from "@/components/core/link";

import { BriefEstimateCard, BriefFiles, BriefSummary, BriefTimeline, StatusChip } from "../campaign-briefs/components";
import { NEXT_STATUSES, STATUS_LABELS } from "../campaign-briefs/constants";

/** One client's request, as AdaptiveX staff work it. */
export function ClientRequestView(): React.JSX.Element {
	const { briefId } = useParams<{ briefId: string }>();
	const { data: detail, isLoading, isError } = useAdminCampaignBrief(briefId);

	if (isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (isError || !detail) {
		return (
			<Box sx={{ p: "var(--Content-padding)" }}>
				<Alert severity="error">This request could not be found.</Alert>
			</Box>
		);
	}

	const { brief } = detail;

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack spacing={3}>
					<Link
						color="text.primary"
						component={RouterLink}
						href="/client-requests"
						sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
						variant="subtitle2"
					>
						<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
						Client requests
					</Link>
					<Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
						<Box>
							<Typography variant="h4">{brief.title}</Typography>
							<Typography variant="body2" color="text.secondary">
								{detail.accountName ?? "Unknown account"}
							</Typography>
						</Box>
						<StatusChip status={brief.status} />
					</Stack>
				</Stack>

				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 8 }}>
						<Stack spacing={3}>
							<BriefSummary detail={detail} />
							<BriefFiles
								brief={brief}
								documents={detail.documents}
								onDownload={(document) =>
									downloadBriefDocument(
										`/admin/campaign-briefs/${brief.id}/documents/${document.id}/download`,
										document.originalFilename
									)
								}
							/>
						</Stack>
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<Stack spacing={3}>
							<MoveStatus briefId={brief.id} status={brief.status} />
							<BriefEstimateCard brief={brief} />
							<BriefTimeline detail={detail} />
						</Stack>
					</Grid>
				</Grid>
			</Stack>
		</Box>
	);
}

function MoveStatus({ briefId, status }: { briefId: string; status: CampaignBriefStatus }): React.JSX.Element {
	const move = useMoveCampaignBrief(briefId);
	const options = NEXT_STATUSES[status];
	const [next, setNext] = React.useState<CampaignBriefStatus | "">("");
	const [note, setNote] = React.useState("");

	return (
		<Card>
			<CardHeader title="Status" />
			<Divider />
			<CardContent>
				{options.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						This request is {STATUS_LABELS[status].toLowerCase()}. Nothing more to do.
					</Typography>
				) : (
					<Stack spacing={2}>
						<FormControl fullWidth>
							<InputLabel id="move-to-label">Move to</InputLabel>
							<Select
								labelId="move-to-label"
								label="Move to"
								value={next}
								onChange={(event) => setNext(event.target.value as CampaignBriefStatus)}
							>
								{options.map((value) => (
									<MenuItem key={value} value={value}>
										{STATUS_LABELS[value]}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Note (the client sees this)"
							multiline
							minRows={2}
							value={note}
							onChange={(event) => setNote(event.target.value)}
						/>
						<Button
							variant="contained"
							disabled={!next || move.isPending}
							onClick={() => {
								if (!next) return;
								move.mutate(
									{ status: next, note: note.trim() || null },
									{
										onSuccess: () => {
											setNext("");
											setNote("");
										},
									}
								);
							}}
						>
							Update status
						</Button>
					</Stack>
				)}
			</CardContent>
		</Card>
	);
}
